import React, { useState } from 'react';
import { Box, Typography, Button, Container, Card, CardContent, CardActions, useMediaQuery } from '@mui/material';
import { PlayArrow, PlayCircleFilled } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { chakraColors } from '../theme/theme';
import NavBar from '../components/NavBar';
import VideoModal from '../components/VideoModal';
import AudioModal from '../components/AudioModal';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useHomeContent } from '../contexts/HomeContentContext';

// (unused helper removed)

const HomePage = () => {
  const { homeContent, loading } = useHomeContent();
  // removed unused menu state
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [audioModalOpen, setAudioModalOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useMuiTheme();
  useMediaQuery(theme.breakpoints.down('md')); // computed but unused; keep call for responsive recalcs if needed
  const { user } = useAuth();
  const firstName = (user?.name || user?.email || 'Friend').split(' ')[0];

  // removed unused menu handlers

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        py: 0
      }}
    >
      <NavBar user={user} />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Personalized Greeting */}
        <Box mb={4}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
            Welcome back, {firstName}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Ready to dive into today's peaceful practice?
          </Typography>
        </Box>

        {/* Top Section - Featured Affirmation + Energy Exchange */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '2fr 1fr'
            },
            gap: 4,
            alignItems: 'stretch',
            mb: 4
          }}
        >
          {/* Featured Affirmation Card */}
          {loading ? (
            <Card sx={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography>Loading today's affirmation...</Typography>
            </Card>
                    ) : homeContent?.featuredAffirmation ? (
            <Card
              sx={{
                minHeight: 300,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
                border: `2px solid ${chakraColors[homeContent.featuredAffirmation.chakra]}40`,
                backgroundImage: homeContent?.featuredAffirmation?.imageUrl ? `url(${homeContent.featuredAffirmation.imageUrl})` : 'none',
                backgroundColor: 'black',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                '&::before': homeContent?.featuredAffirmation?.imageUrl ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  zIndex: 1
                } : {}
              }}
            >
              <CardContent sx={{ 
                p: 4, 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                zIndex: 2
              }}>
                <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: 'white', mb: 2 }}>
                  Today's Affirmation
                </Typography>

                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: homeContent?.featuredAffirmation?.textColor || 'white',
                    fontWeight: 500,
                    fontFamily: homeContent?.featuredAffirmation?.fontFamily || 'Montserrat, Arial, sans-serif',
                    lineHeight: 1.4,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    fontStyle: 'italic'
                  }}
                >
                  "{homeContent.featuredAffirmation.text}"
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography>Loading featured affirmation...</Typography>
            </Card>
          )}

          {/* Energy Exchange Card (replaces What's New) */}
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 300,
              bgcolor: 'background.paper',
              color: 'text.primary',
              boxShadow: 4,
              transition: 'transform 0.18s, box-shadow 0.18s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1, pb: 1, pt: 3, px: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2, color: '#6A4C93' }}>
                🛍️ Energy Exchange
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Discover personalized mantras, eBooks, and healing sessions to deepen your practice. Unlock Premium to access our complete library of 100+ mantras, exclusive sleep meditations, and premium features.
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-start', px: 3, pb: 2, pt: 0 }}>
              <Button size="small" onClick={() => navigate('/products')}>
                Go to Shop
              </Button>
            </CardActions>
          </Card>
        </Box>

        {/* Feature Cards Section (bottom) */}
        <Typography variant="h5" gutterBottom sx={{ mt: 2, mb: 3, fontWeight: 600 }}>
          Continue Your Journey
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)'
            },
            gap: 4,
            alignItems: 'stretch'
          }}
        >
          {/* Featured Video */}
          <Card sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            backgroundImage: homeContent?.featuredVideo?.backgroundImageUrl ? `url(${homeContent.featuredVideo.backgroundImageUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            '&::before': homeContent?.featuredVideo?.backgroundImageUrl ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1
            } : {}
          }}>
            <CardContent sx={{ 
              flexGrow: 1,
              position: 'relative',
              zIndex: 2
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                color: homeContent?.featuredVideo?.backgroundImageUrl ? 'white' : 'primary.main',
                textShadow: homeContent?.featuredVideo?.backgroundImageUrl ? '1px 1px 3px rgba(0,0,0,0.7)' : 'none'
              }}>
                🎥 Featured Video
              </Typography>
              {loading ? (
                <Typography variant="body2" sx={{ 
                  color: homeContent?.featuredVideo?.backgroundImageUrl ? 'white' : 'text.primary' 
                }}>
                  Loading...
                </Typography>
              ) : homeContent?.featuredVideo ? (
                <>
                  <Typography variant="subtitle1" gutterBottom fontWeight={500} sx={{
                    color: homeContent?.featuredVideo?.backgroundImageUrl ? 'white' : 'text.primary',
                    textShadow: homeContent?.featuredVideo?.backgroundImageUrl ? '1px 1px 3px rgba(0,0,0,0.7)' : 'none'
                  }}>
                    {homeContent.featuredVideo.title}
                  </Typography>
                  <Typography variant="body2" paragraph sx={{
                    color: homeContent?.featuredVideo?.backgroundImageUrl ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                    textShadow: homeContent?.featuredVideo?.backgroundImageUrl ? '1px 1px 3px rgba(0,0,0,0.7)' : 'none'
                  }}>
                    {homeContent.featuredVideo.excerpt}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ 
                  color: homeContent?.featuredVideo?.backgroundImageUrl ? 'white' : 'text.primary' 
                }}>
                  No featured video available
                </Typography>
              )}
            </CardContent>
            <CardActions sx={{ 
              position: 'relative',
              zIndex: 2
            }}>
              <Button 
                size="small" 
                startIcon={<PlayCircleFilled />} 
                onClick={() => setVideoModalOpen(true)}
                disabled={!homeContent?.featuredVideo}
                sx={{
                  color: homeContent?.featuredVideo?.backgroundImageUrl ? 'white' : 'primary.main',
                  backgroundColor: homeContent?.featuredVideo?.backgroundImageUrl ? 'rgba(255,255,255,0.2)' : 'transparent',
                  '&:hover': {
                    backgroundColor: homeContent?.featuredVideo?.backgroundImageUrl ? 'rgba(255,255,255,0.3)' : 'action.hover'
                  }
                }}
              >
                Watch Now
              </Button>
            </CardActions>
          </Card>

          {/* Featured Audio */}
          <Card sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            backgroundImage: homeContent?.featuredAudio?.backgroundImageUrl ? `url(${homeContent.featuredAudio.backgroundImageUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            '&::before': homeContent?.featuredAudio?.backgroundImageUrl ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1
            } : {}
          }}>
            <CardContent sx={{ 
              flexGrow: 1,
              position: 'relative',
              zIndex: 2
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                color: homeContent?.featuredAudio?.backgroundImageUrl ? 'white' : '#4CAF50',
                textShadow: homeContent?.featuredAudio?.backgroundImageUrl ? '1px 1px 3px rgba(0,0,0,0.7)' : 'none'
              }}>
                🎵 Featured Audio
              </Typography>
              {loading ? (
                <Typography variant="body2" sx={{ 
                  color: homeContent?.featuredAudio?.backgroundImageUrl ? 'white' : 'text.primary' 
                }}>
                  Loading...
                </Typography>
              ) : homeContent?.featuredAudio ? (
                <>
                  <Typography variant="subtitle1" gutterBottom fontWeight={500} sx={{
                    color: homeContent?.featuredAudio?.backgroundImageUrl ? 'white' : 'text.primary',
                    textShadow: homeContent?.featuredAudio?.backgroundImageUrl ? '1px 1px 3px rgba(0,0,0,0.7)' : 'none'
                  }}>
                    {homeContent.featuredAudio.title}
                  </Typography>
                  <Typography variant="body2" paragraph sx={{
                    color: homeContent?.featuredAudio?.backgroundImageUrl ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                    textShadow: homeContent?.featuredAudio?.backgroundImageUrl ? '1px 1px 3px rgba(0,0,0,0.7)' : 'none'
                  }}>
                    {homeContent.featuredAudio.excerpt}
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: homeContent?.featuredAudio?.backgroundImageUrl ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                    textShadow: homeContent?.featuredAudio?.backgroundImageUrl ? '1px 1px 3px rgba(0,0,0,0.7)' : 'none'
                  }}>
                    {homeContent.featuredAudio.duration} • {homeContent.featuredAudio.authorName}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ 
                  color: homeContent?.featuredAudio?.backgroundImageUrl ? 'white' : 'text.primary' 
                }}>
                  No featured audio available
                </Typography>
              )}
            </CardContent>
            <CardActions sx={{ 
              position: 'relative',
              zIndex: 2
            }}>
              <Button 
                size="small" 
                startIcon={<PlayArrow />} 
                onClick={() => setAudioModalOpen(true)}
                disabled={!homeContent?.featuredAudio}
                sx={{
                  color: homeContent?.featuredAudio?.backgroundImageUrl ? 'white' : '#4CAF50',
                  backgroundColor: homeContent?.featuredAudio?.backgroundImageUrl ? 'rgba(255,255,255,0.2)' : 'transparent',
                  '&:hover': {
                    backgroundColor: homeContent?.featuredAudio?.backgroundImageUrl ? 'rgba(255,255,255,0.3)' : 'action.hover'
                  }
                }}
              >
                Listen Now
              </Button>
            </CardActions>
          </Card>

          {/* Journal Shortcut */}
          <Card sx={{ display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom color="secondary">
                📔 My Journal
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Reflect on your practice and track your spiritual growth with guided prompts.
              </Typography>
            </CardContent>
            <CardActions>
              <Button size="small" onClick={() => navigate('/journal')}>
                Go to Journal
              </Button>
            </CardActions>
          </Card>
        </Box>
      </Container>

      {/* Featured Video Modal */}
      <VideoModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        title={homeContent?.featuredVideo?.title || ''}
        videoUrl={homeContent?.featuredVideo?.videoUrl || ''}
        content={homeContent?.featuredVideo?.fullContent || ''}
        excerpt={homeContent?.featuredVideo?.excerpt || ''}
      />

      {/* Featured Audio Modal */}
      <AudioModal
        open={audioModalOpen}
        onClose={() => setAudioModalOpen(false)}
        title={homeContent?.featuredAudio?.title || ''}
        audioUrl={homeContent?.featuredAudio?.audioUrl || ''}
        content={homeContent?.featuredAudio?.fullContent || ''}
        excerpt={homeContent?.featuredAudio?.excerpt || ''}
      />
    </Box>
  );
};

export default HomePage; 